import { useMutation, useQueryClient } from '@tanstack/react-query';
import { solicitacaoService } from '../../services/solicitacaoService';
import { movimentacaoService } from '../../services/movimentacaoService';
import type {
  SolicitacaoCreate,
  SolicitacaoUpdate,
  SolicitacaoOperacionalUpdate,
  SolicitacaoCancelamento,
  SolicitacaoReativacao,
  SolicitacaoExclusao,
  SolicitacaoVincularColaborador,
} from '../../models/solicitacao.types';
import type { MovimentacaoCreate } from '../../models/movimentacao.types';
import { SI_SOLICITACOES_QUERY_KEYS } from './useSolicitacoesQuery';
import { useToast } from '@/hooks/useToast';

/**
 * Mutations de Solicitação — invalidação de cache ao invés de optimistic update
 * (padrão OKR simplificado), suficiente para o MVP do SI.
 */
export function useSolicitacaoMutations() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const invalidarTudo = () =>
    queryClient.invalidateQueries({ queryKey: SI_SOLICITACOES_QUERY_KEYS.all });

  const criarSolicitacao = useMutation({
    mutationFn: (data: SolicitacaoCreate) => solicitacaoService.create(data),
    onSuccess: invalidarTudo,
    onError: (error: any) => {
      toast.error(`Erro ao criar solicitação: ${error?.message || 'erro desconhecido'}`);
    },
  });

  /**
   * Cria a Solicitação e, em seguida, a primeira Movimentação sob ela — um único clique
   * pra quem abre o pedido (não precisa navegar até a tela de detalhes pra criar a
   * Movimentação à parte). A autorização de criar essa 1ª Movimentação com campos de
   * intake é a mesma de criar a própria Solicitação (ver firestore.rules).
   */
  const criarSolicitacaoComMovimentacao = useMutation({
    mutationFn: async ({
      solicitacao,
      movimentacao,
      movimentacaoDevolucao,
    }: {
      solicitacao: SolicitacaoCreate;
      /** Ausente só pros tipos em TIPOS_SOLICITACAO_SEM_MOVIMENTACAO (ex.: Formatação) —
       * um chamado pro SI sem remessa/logística de equipamento associada. */
      movimentacao?: MovimentacaoCreate;
      /** Só em Mudança de Projeto/Troca de Equipamento — cria a 2ª Movimentação
       * (devolução) junto, já que esses tipos sempre exigem as duas (ver
       * NovaSolicitacaoContent). `equipamentoSugeridoId` no item de intake, se presente,
       * é aplicado depois automaticamente (onMovimentacaoWriteAplicarEquipamentoSugerido). */
      movimentacaoDevolucao?: MovimentacaoCreate;
    }) => {
      const solicitacaoId = await solicitacaoService.create(solicitacao);

      try {
        // dispararTransicao = false: essa é a abertura do pedido pelo solicitante, não
        // trabalho do SI — ver comentário em movimentacaoService.create.
        if (movimentacao) {
          await movimentacaoService.create(solicitacaoId, movimentacao, false);
        }
        if (movimentacaoDevolucao) {
          await movimentacaoService.create(solicitacaoId, movimentacaoDevolucao, false);
        }
      } catch (error: any) {
        // Diferencia de qual das escritas veio o erro — a Solicitação já foi criada
        // com sucesso nesse ponto (fica visível em "Que eu abri" mesmo se a Movimentação falhar).
        console.error('Solicitação criada, mas falhou ao criar Movimentação', {
          solicitacaoId,
          movimentacao,
          movimentacaoDevolucao,
          error,
        });
        throw new Error(
          `Solicitação criada, mas a Movimentação falhou: ${error?.message ?? error?.code ?? 'erro desconhecido'}`
        );
      }

      return solicitacaoId;
    },
    onSuccess: invalidarTudo,
    onError: (error: any) => {
      toast.error(`Erro ao criar solicitação: ${error?.message || 'erro desconhecido'}`);
    },
  });

  const atualizarSolicitacao = useMutation({
    mutationFn: (data: SolicitacaoUpdate) => solicitacaoService.update(data),
    onSuccess: invalidarTudo,
    onError: (error: any) => {
      toast.error(`Erro ao atualizar solicitação: ${error?.message || 'erro desconhecido'}`);
    },
  });

  const atualizarOperacional = useMutation({
    mutationFn: (data: SolicitacaoOperacionalUpdate) => solicitacaoService.updateOperacional(data),
    onSuccess: invalidarTudo,
    onError: (error: any) => {
      toast.error(`Erro ao atualizar dados operacionais: ${error?.message || 'erro desconhecido'}`);
    },
  });

  const cancelarSolicitacao = useMutation({
    mutationFn: (data: SolicitacaoCancelamento) => solicitacaoService.cancelar(data),
    onSuccess: () => {
      invalidarTudo();
      toast.success('Solicitação cancelada.');
    },
    onError: (error: any) => {
      toast.error(`Erro ao cancelar solicitação: ${error?.message || 'erro desconhecido'}`);
    },
  });

  const reativarSolicitacao = useMutation({
    mutationFn: (data: SolicitacaoReativacao) => solicitacaoService.reativar(data),
    onSuccess: () => {
      invalidarTudo();
      toast.success('Solicitação reativada.');
    },
    onError: (error: any) => {
      toast.error(`Erro ao reativar solicitação: ${error?.message || 'erro desconhecido'}`);
    },
  });

  const excluirSolicitacao = useMutation({
    mutationFn: (data: SolicitacaoExclusao) => solicitacaoService.excluir(data),
    onSuccess: () => {
      invalidarTudo();
      toast.success('Solicitação excluída.');
    },
    onError: (error: any) => {
      toast.error(`Erro ao excluir solicitação: ${error?.message || 'erro desconhecido'}`);
    },
  });

  const vincularColaborador = useMutation({
    mutationFn: (data: SolicitacaoVincularColaborador) => solicitacaoService.vincularColaborador(data),
    onSuccess: () => {
      invalidarTudo();
      toast.success('Colaborador vinculado com sucesso.');
    },
    onError: (error: any) => {
      toast.error(`Erro ao vincular colaborador: ${error?.message || 'erro desconhecido'}`);
    },
  });

  return {
    criarSolicitacao,
    criarSolicitacaoComMovimentacao,
    atualizarSolicitacao,
    atualizarOperacional,
    cancelarSolicitacao,
    reativarSolicitacao,
    excluirSolicitacao,
    vincularColaborador,
  };
}
